import React, { useState } from 'react';

interface BarData {
    label: string;
    value: number;
}

interface HorizontalBarChartProps {
    data: BarData[];
    title: string;
    subtitle?: string;
    showPercentages?: boolean;
    maxBars?: number;
    sortBy?: 'value' | 'label';
}

export const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({
    data,
    title,
    subtitle,
    showPercentages = true,
    maxBars,
    sortBy = 'value',
}) => {
    const [showAll, setShowAll] = useState(false);

    if (!data || data.length === 0) {
        return (
            <div className="p-5 rounded-xl border-2 border-border/50 bg-card">
                <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground">No data available</p>
            </div>
        );
    }

    const sorted = [...data].sort((a, b) =>
        sortBy === 'value' ? b.value - a.value : a.label.localeCompare(b.label)
    );

    const total = sorted.reduce((sum, d) => sum + d.value, 0);
    const hasMore = maxBars && sorted.length > maxBars;
    const displayed = hasMore && !showAll ? sorted.slice(0, maxBars) : sorted;
    const maxCount = Math.max(...displayed.map(d => d.value));

    return (
        <div className="p-5 rounded-xl border-2 border-border/50 bg-card">
            <h3 className="text-lg font-semibold text-foreground mb-1">{title}</h3>
            {subtitle && <p className="text-sm text-muted-foreground mb-4">{subtitle}</p>}
            <div className="space-y-3 mt-4">
                {displayed.map((item) => {
                    const widthPct = maxCount > 0 ? (item.value / maxCount) * 100 : 0;
                    const pct = total > 0 ? Math.round((item.value / total) * 100) : 0;
                    return (
                        <div key={item.label} className="flex items-center gap-3">
                            <span className="text-sm text-muted-foreground w-40 text-right flex-shrink-0 truncate" title={item.label}>
                                {item.label}
                            </span>
                            <div className="flex-1 bg-muted/30 rounded-full h-7 overflow-hidden">
                                <div
                                    className="h-full bg-primary/60 rounded-full transition-all duration-500"
                                    style={{ width: `${Math.max(widthPct, 2)}%` }}
                                />
                            </div>
                            <span className="text-sm font-medium text-foreground w-16 text-right flex-shrink-0">
                                {item.value}{showPercentages ? ` (${pct}%)` : ''}
                            </span>
                        </div>
                    );
                })}
            </div>
            {hasMore && (
                <button
                    onClick={() => setShowAll(!showAll)}
                    className="mt-3 text-sm text-primary hover:underline"
                >
                    {showAll ? 'Show less' : `Show all ${sorted.length}`}
                </button>
            )}
        </div>
    );
};
